import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useState } from "react";
import { Link2, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";

function LinkRow({ item, products, byId, canEdit, onSave }) {
  const [q, setQ] = usePersistedState("price-list-links.q", "");
  const ids = item.billing_product_ids;
  const matches = q.trim() ? products.filter((p) => !ids.includes(p.product_id) && p.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6) : [];
  const chip = (pid, extra) => (
    <span key={pid} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${extra}`}>{byId[pid]?.name || pid}</span>
  );
  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`billing-link-row-${item.item_id}`}>
      <td className="w-1/3 px-2 py-2 text-xs">{item.name}<div className="text-[10px] text-slate-400">{item.category}</div></td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap gap-1">
          {ids.map((pid) => (
            <span key={pid} className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] text-sky-800" data-testid={`billing-link-chip-${item.item_id}-${pid}`}>
              {byId[pid]?.name || pid}
              {canEdit && <button type="button" onClick={() => onSave(item, ids.filter((x) => x !== pid))} data-testid={`billing-link-remove-${item.item_id}-${pid}`}><X className="h-3 w-3" /></button>}
            </span>
          ))}
          {!ids.length && <span className="text-[11px] text-slate-400">Not linked</span>}
        </div>
        {canEdit && item.suggested.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-slate-500"><Sparkles className="h-3 w-3 text-amber-500" /> Suggested:
            {item.suggested.map((pid) => (
              <button key={pid} type="button" onClick={() => onSave(item, [...ids, pid])} data-testid={`billing-link-suggest-${item.item_id}-${pid}`}>
                {chip(pid, "border border-dashed border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100")}</button>
            ))}
          </div>
        )}
        {canEdit && (
          <div className="relative mt-1 w-64">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add billing product…" className="h-7 text-xs" data-testid={`billing-link-search-${item.item_id}`} />
            {matches.length > 0 && (
              <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
                {matches.map((p) => (
                  <button key={p.product_id} type="button" className="block w-full px-2 py-1 text-left text-xs hover:bg-slate-50"
                          onClick={() => { setQ(""); onSave(item, [...ids, p.product_id]); }} data-testid={`billing-link-add-${item.item_id}-${p.product_id}`}>
                    {p.name}{p.status && p.status !== "Active" ? <span className="text-slate-400"> · {p.status}</span> : null}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

/** Link each Price List item to the billing products it's invoiced as. */
export function BillingLinksTab() {
  const [d, setD] = useState(null);
  const load = () => api.get("/price-list/billing-links").then(({ data }) => setD(data))
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load billing links"));
  useEffect(() => { load(); }, []);
  const byId = useMemo(() => Object.fromEntries((d?.products || []).map((p) => [p.product_id, p])), [d]);
  const save = async (item, ids) => {
    try { await api.put(`/price-list/items/${item.item_id}/billing-links`, { product_ids: ids }); toast.success("Billing link saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
  };
  if (!d) return <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-slate-300" />;
  const linked = d.items.filter((i) => i.billing_product_ids.length).length;
  return (
    <div className="space-y-2" data-testid="price-billing-links">
      <p className="flex items-center gap-1.5 text-xs text-slate-600" data-testid="billing-links-count">
        <Link2 className="h-3.5 w-3.5 text-slate-400" /> {linked} of {d.items.length} items linked to billing products</p>
      <div className="max-h-[55vh] overflow-auto rounded-md border border-slate-200">
        <table className="w-full"><tbody>
          {d.items.map((i) => <LinkRow key={i.item_id} item={i} products={d.products} byId={byId} canEdit={d.can_edit} onSave={save} />)}
        </tbody></table>
      </div>
    </div>
  );
}
